import { Scene } from 'phaser'
import type { Types, Physics, GameObjects } from 'phaser'

import events from './events'
import * as images from './images'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const debug = require('debug')('app:scenes:Arena')

const DEFAULT_WIDTH = 512
const DEFAULT_HEIGHT = 523
const CENTER_X = DEFAULT_WIDTH / 2
const CENTER_Y = DEFAULT_HEIGHT / 2
const CHAR_WIDTH = 30

const MOVE_SPEED = 2.8
const SHIFT_SPEED = 0.6

export const ACTION_FIGHTING = 'fight'

const MAX_CONTACT_SHIFTING = 80

export enum ArenaState {
  Idle,
  Fighting,
  LeftGladiatorWin,
  RightGladiatorWin,
  Draw,
}

enum ShiftingDirection {
  Left = -1,
  Right = 1,
}

export enum ArenaGladiator {
  None = -1,
  Left = 0,
  Right = 1,
}

export enum ArenaEvents {
  Ready = 'arena:ready',
  StateChange = 'arena:change',
  SelectGladitor = 'arena:select',
}

export default class Arena extends Scene {
  private status = ArenaState.Idle
  private selected = ArenaGladiator.None

  private gladiators: Types.Physics.Arcade.SpriteWithDynamicBody[] = []
  private auras: GameObjects.Image[] = []
  private results: GameObjects.Image[] = []

  private contactPointOrigin = DEFAULT_WIDTH / 2
  private contactPoint = DEFAULT_WIDTH / 2
  private contactPointShiftingDirection = ShiftingDirection.Left
  private leftBoundary = this.contactPointOrigin - 80
  private rightBoundary = this.contactPointOrigin + 80

  constructor(config: Types.Scenes.SettingsConfig) {
    super(config)

    this.resetLeftBoundary()
    this.resetRightBoundary()
  }

  preload() {
    this.load.image('background', images.background)

    // this.load.spritesheet('gladiator-01', images.gladiator01, { frameWidth: 196, frameHeight: 173 })
    this.load.image('gladiator-left', images.gladiator1)
    this.load.image('gladiator-right', images.gladiator2)

    this.load.image('aura-left', images.aura1)
    this.load.image('aura-right', images.aura2)

    this.load.image('result-draw', images.result0)
    this.load.image('result-left-win', images.result1)
    this.load.image('result-right-win', images.result2)

    // TODO: VFX
    // this.load.image('vfx-slash', images.vfxSlash)
    // this.load.image('vfx-dash', images.vfxDash)
  }

  create() {
    // The background and floor
    this.add.image(0, 0, 'background').setOrigin(0, 0)
    this.physics.world.setBounds(DEFAULT_WIDTH * -2, -1000, DEFAULT_WIDTH * 5, 1000 + DEFAULT_HEIGHT - 160)
    this.physics.world.on('worldbounds', this.stopObjectVelocity)

    this.auras = ['aura-left', 'aura-right'].map((res, i) => {
      const image = this.add.image(0, 0, res)
      image.setVisible(false)
      return image
    })

    // Characters
    const Gladiator1 = this.physics.add.sprite(
      -60,
      80,
      'gladiator-left'
    )
    Gladiator1.setVelocityX(400)

    const Gladiator2 = this.physics.add.sprite(
      DEFAULT_HEIGHT + 60,
      80,
      'gladiator-right'
    )
    Gladiator2.setVelocityX(-400)

    this.gladiators = [Gladiator1, Gladiator2]
    this.gladiators.forEach((g, i) => {
      g.setBounce(.2)
      g.setCollideWorldBounds(true)
      g.body.onWorldBounds = true
      g.setInteractive()
      // g.setScale(1.4, 1.4)
      g.on('pointerup', function (this: Arena) {
        debug('Select gladiator %d', i)
        this.select(i)
      }, this)
    })

    this.results = ['result-draw', 'result-left-win', 'result-right-win'].map((r) => {
      const image = this.add.image(0, 0, r)
      image.setVisible(false)
      return image
    })

    events.emit(ArenaEvents.Ready, this)
    events.emit(ArenaEvents.StateChange, ArenaState.Idle )
  }

  public fight() {
    this.status = ArenaState.Fighting
  }

  public announceWinner(winner: ArenaGladiator) {
    const [left, right] = this.gladiators
    const [resultDraw, resultLeftWin, resultRightWin] = this.results

    if (winner === ArenaGladiator.None) {
      this.status = ArenaState.Draw
      this.jumpTo(left, left.x - 80)
      this.jumpTo(right, right.x + 80)

      resultDraw.setVisible(true)
      resultDraw.setPosition((left.x + right.x) / 2, (left.y + right.y) / 2 - 60)
      resultDraw.setAlpha(0)
      this.cameras.main.zoomTo(1.5, 600, 'Sine.easeOut')
      this.cameras.main.pan(resultDraw.x, resultDraw.y + 30, 600, 'Sine.easeOut')
    } else {
      const [focus, flyaway, direction, status, image] = winner === ArenaGladiator.Left
        ? [left, right, 1, ArenaState.LeftGladiatorWin, resultLeftWin]
        : [right, left, -1, ArenaState.RightGladiatorWin, resultRightWin]

      this.status = status
      this.cameras.main.zoomTo(1.8, 600, 'Sine.easeOut')
      this.cameras.main.pan(focus.x, focus.y + 30, 600, 'Sine.easeOut')
      flyaway.setVelocity(900 * direction, -900)
      flyaway.setAngularVelocity(1800 * direction)
      image.setVisible(true)
      image.setAlpha(0)
      image.setPosition(focus.x, focus.y)
    }
  }

  public reset() {
    const [left, right] = this.gladiators
    left.setVelocity(0, 0)
    left.setAngularVelocity(0)
    left.setAngle(0)
    right.setVelocity(0, 0)
    right.setAngularVelocity(0)
    right.setAngle(0)

    this.jumpTo(left, 120)
    this.jumpTo(right, DEFAULT_WIDTH - 120)

    this.cameras.main.pan(CENTER_X, CENTER_Y, 600, 'Sine.easeInOut')
    this.cameras.main.zoomTo(1, 600, 'Sine.easeInOut')
    this.results.forEach((r) => r.setVisible(false))

    this.status = ArenaState.Idle
    events.emit(ArenaEvents.StateChange, this.status)
  }

  public select(gladiator: ArenaGladiator) {
    if (
      this.status !== ArenaState.Idle ||
      this.selected === gladiator
    ) return

    this.selected = gladiator
    this.auras.forEach((a, i) => a.setVisible(i === gladiator))
    events.emit(ArenaEvents.SelectGladitor, gladiator)
  }

  protected jumpTo(obj: Types.Physics.Arcade.SpriteWithDynamicBody, x: number) {
    const diffX = x - obj.x
    if (Math.abs(diffX) < 1) {
      return
    }

    const direction = diffX / Math.abs(diffX)
    const g = this.physics.world.gravity.y
    const Vx = Math.sqrt(Math.round(Math.abs(diffX))) * 30 * direction //  Math.round(diffX * 2)
    // Vx * t = DistanceX             ==> t = DistanceX / Vx
    // Vy * t + gravity * t^2 / 2 = 0 ==> Vy = - gravity * t / 2 = - gravity * DistanceX / 2Vx
    const Vy = -1 * Math.abs(diffX) * g / Math.abs(Vx * 2)
    obj.setVelocity(Vx, Vy)
  }

  update(time: number, delta: number): void {
    const [left, right] = this.gladiators

    switch (this.status) {
      case ArenaState.Fighting:
        if (this.gladiatorsHasContact()) {
          this.shiftContactPoint()
          left.x = this.contactPoint - CHAR_WIDTH
          right.x = this.contactPoint + CHAR_WIDTH
        } else {
          left.x += Math.min(
            MOVE_SPEED,
            this.contactPoint - CHAR_WIDTH - left.x
          )
          right.x -= Math.min(
            MOVE_SPEED,
            right.x - this.contactPoint - CHAR_WIDTH
          )
        }
        break
      case ArenaState.LeftGladiatorWin:
        const leftWin = this.results[1]
        leftWin.alpha = Math.min(1, leftWin.alpha + leftWin.alpha * .1 + .0001)
        break
      case ArenaState.RightGladiatorWin:
        const rightWin = this.results[2]
        rightWin.alpha = Math.min(1, rightWin.alpha + rightWin.alpha * .1 + .0001)
        break
      case ArenaState.Draw:
        const draw = this.results[0]
        draw.alpha = Math.min(1, draw.alpha + draw.alpha * .1 + 0.0001)
        break
      // case ArenaState.Idle:
      //   break
      default:
        break
    }

    const [auraLeft, auraRight] = this.auras
    auraLeft.setPosition(left.x - 10, DEFAULT_HEIGHT - 200)
    auraRight.setPosition(right.x + 10, DEFAULT_HEIGHT - 200)
  }

  protected gladiatorsHasContact(): boolean {
    return (
      this.gladiators[ArenaGladiator.Left].x >= this.contactPoint - CHAR_WIDTH &&
      this.gladiators[ArenaGladiator.Right].x <= this.contactPoint + CHAR_WIDTH
    )
  }

  protected resetRightBoundary() {
    const rnd = Math.random() * 60 - 40
    this.rightBoundary = Math.max(
      this.contactPoint,
      this.contactPointOrigin + MAX_CONTACT_SHIFTING + rnd
    )
  }

  protected resetLeftBoundary() {
    const rnd = Math.random() * 60 - 20
    this.rightBoundary = Math.min(
      this.contactPoint,
      this.contactPointOrigin - MAX_CONTACT_SHIFTING + rnd
    )
  }

  protected reverseShiftingDirection() {
    this.contactPointShiftingDirection =
      this.contactPointShiftingDirection === ShiftingDirection.Left
        ? ShiftingDirection.Right
        : ShiftingDirection.Left

    if (this.contactPointShiftingDirection === ShiftingDirection.Right) {
      this.resetRightBoundary()
    } else {
      this.resetLeftBoundary()
    }
  }

  protected shiftContactPoint() {
    if (Math.random() < 0.0001) this.reverseShiftingDirection()

    const boundary =
      this.contactPointShiftingDirection === ShiftingDirection.Left
        ? this.leftBoundary
        : this.rightBoundary

    const distance = boundary - this.contactPoint
    const actual =
      Math.min(Math.abs(distance * 0.4), SHIFT_SPEED) *
      this.contactPointShiftingDirection
    this.contactPoint += actual

    if (Math.abs(actual) < 0.6 && Math.random() < 0.02)
      this.reverseShiftingDirection()
  }

  protected stopObjectVelocity(body: Physics.Arcade.Body) {
    if (body.onFloor()) {
      const gladiator = body.gameObject as Types.Physics.Arcade.SpriteWithDynamicBody
      gladiator.setVelocityX(0)
      gladiator.setAngularVelocity(0)
    }
  }
}
